/**
 * Three calm onboarding slides introducing the product. No deceptive claims,
 * no paywall — the flow ends on the role selection page.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui'
import { BreathIcon, PlannerIcon, RememberIcon } from '@/components/icons'
import { PATHS } from '@/app/router/paths'
import { AuthLayout, AuthLegalFooter, RustWordmark } from './AuthLayout'

const SLIDES = [
  {
    id: 'planner',
    Icon: PlannerIcon,
    title: 'Je dag in vaste blokken',
    text: 'Plan je dag met duidelijke activiteiten en tijdsblokken. Zo weet je altijd wat er nu aan de beurt is.',
  },
  {
    id: 'onthouden',
    Icon: RememberIcon,
    title: 'Niets meer vergeten',
    text: 'Leg herinneringen, boodschappen en vaste plekken van spullen vast. RUST bewaart het overzicht voor je.',
  },
  {
    id: 'adem',
    Icon: BreathIcon,
    title: 'Ruimte om op adem te komen',
    text: 'Rustige ademhalingsoefeningen en een dagboek helpen je de dag te verwerken, wanneer jij dat wilt.',
  },
] as const

export default function OnboardingPage() {
  const navigate = useNavigate()
  const [index, setIndex] = useState(0)
  const slide = SLIDES[index]
  const isLast = index === SLIDES.length - 1

  return (
    <AuthLayout backTo={PATHS.welcome} backLabel="Terug naar welkom">
      <RustWordmark width={140} />
      <div
        key={slide.id}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 16 }}
        aria-live="polite"
      >
        <span
          aria-hidden="true"
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.16)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
          }}
        >
          <slide.Icon size={36} />
        </span>
        <h1 className="auth-headline" style={{ fontSize: 26 }}>
          {slide.title}
        </h1>
        <p className="auth-subtitle">{slide.text}</p>
      </div>

      <div className="auth-dots" role="tablist" aria-label={`Stap ${index + 1} van ${SLIDES.length}`}>
        {SLIDES.map((s, i) => (
          <button
            key={s.id}
            type="button"
            className="auth-dot"
            data-active={i === index}
            aria-label={`Ga naar stap ${i + 1}`}
            aria-current={i === index}
            onClick={() => setIndex(i)}
          />
        ))}
      </div>

      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={() => (isLast ? navigate(PATHS.role) : setIndex(index + 1))}
        >
          {isLast ? 'Aan de slag' : 'Volgende'}
        </Button>
        {!isLast && (
          <Button variant="ghost" fullWidth onClick={() => navigate(PATHS.role)}>
            Overslaan
          </Button>
        )}
      </div>
      <AuthLegalFooter />
    </AuthLayout>
  )
}
